import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Vault, KeyRound, XCircle, RotateCcw, Loader2, ShieldCheck,
  Clock, CheckCircle2, AlertTriangle, Copy,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const TABS = [
  { key: "requested",  label: "Awaiting Review" },
  { key: "key_issued", label: "Key Issued" },
  { key: "activated",  label: "Activated" },
  { key: "rejected",   label: "Rejected" },
  { key: "expired",    label: "Expired" },
];

const STATUS_STYLE = {
  requested:  { bg: "bg-[#B87333]/15", fg: "text-[#B87333]", border: "border-[#B87333]/40", label: "Requested" },
  key_issued: { bg: "bg-[#D8B23E]/15", fg: "text-[#D8B23E]", border: "border-[#D8B23E]/40", label: "Key Issued" },
  activated:  { bg: "bg-[#2FB999]/15", fg: "text-[#2FB999]", border: "border-[#2FB999]/40", label: "Activated" },
  rejected:   { bg: "bg-[#E5484D]/15", fg: "text-[#E5484D]", border: "border-[#E5484D]/40", label: "Rejected" },
  expired:    { bg: "bg-white/5",       fg: "text-[#8A8A82]", border: "border-white/10",    label: "Expired" },
};

export function AdminTreasuryDesk({ api }) {
  const [tab, setTab] = useState("requested");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [issueRow, setIssueRow] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/treasury/requests?status=${tab}`);
      setRows(r.data?.rows || []);
      setCounts(r.data?.counts || {});
    } catch { toast.error("Could not load treasury queue"); }
    finally { setLoading(false); }
  }, [api, tab]);

  useEffect(() => { load(); }, [load]);

  const issueKey = async (row) => {
    setBusyId(row.id);
    try {
      const r = await api.post(`/admin/treasury/${row.id}/issue-key`);
      setIssueRow({ ...row, ...r.data });
      toast.success("Activation Key dispatched", {
        description: `${row.user_email} · Vinci email fired · 24h TTL`,
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not issue key");
    } finally { setBusyId(null); }
  };

  const submitReject = async () => {
    if (!rejectRow) return;
    setBusyId(rejectRow.id);
    try {
      await api.post(`/admin/treasury/${rejectRow.id}/reject`,
                     { reason: rejectReason.trim() });
      toast.success("Request rejected");
      setRejectRow(null); setRejectReason("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reject");
    } finally { setBusyId(null); }
  };

  const resetPartner = async (row) => {
    setBusyId(row.id);
    try {
      await api.post(`/admin/treasury/${row.id}/reset`);
      toast.success("Partner unlocked · they may request again");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reset");
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-treasury-desk">
      {/* Header */}
      <div className="border border-[#D8B23E]/30 bg-gradient-to-br from-[#0A0906] via-[#1B1401]/40 to-[#0A0906] rounded-sm p-6 md:p-8">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full grid place-items-center bg-[#D8B23E]/15 border border-[#D8B23E]/40">
            <Vault size={20} className="text-[#D8B23E]" />
          </div>
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#D8B23E]">
              Vinci Finance Desk
            </div>
            <h2 className="font-serif text-3xl mt-1.5 text-[#F5F5F0]">Treasury Desk</h2>
            <p className="text-[13px] text-[#8A8A82] font-body mt-1 max-w-3xl">
              Privileged induction queue for bank-linking. Review the partner's
              trading credentials, then dispatch a monumental Activation Key —
              or veto the request when the signal isn't there.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="treasury-tabs">
        {TABS.map((t) => {
          const count = counts[t.key] || 0;
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              data-testid={`treasury-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-sm font-grotesk text-[10px] uppercase tracking-[0.24em] border transition-colors inline-flex items-center gap-2 ${
                active
                  ? "bg-[#D8B23E]/15 border-[#D8B23E] text-[#D8B23E]"
                  : "bg-transparent border-white/10 text-[#8A8A82] hover:border-[#D8B23E]/40 hover:text-[#D8B23E]"
              }`}
            >
              {t.label}
              <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-black/40 border border-white/10">
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Rows */}
      {loading ? (
        <div className="text-center py-10 text-[#8A8A82]">
          <Loader2 className="animate-spin mx-auto" />
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-sm p-10 text-center text-[#6f6f68] font-body italic">
          No {TABS.find(t => t.key === tab)?.label.toLowerCase()} requests.
        </div>
      ) : (
        <div className="space-y-3" data-testid="treasury-rows">
          {rows.map((row) => (
            <TreasuryRow
              key={row.id}
              row={row}
              busy={busyId === row.id}
              onIssue={() => issueKey(row)}
              onReject={() => { setRejectRow(row); setRejectReason(""); }}
              onReset={() => resetPartner(row)}
            />
          ))}
        </div>
      )}

      {/* Issue-Key Confirmation Dialog */}
      <Dialog open={!!issueRow} onOpenChange={(v) => !v && setIssueRow(null)}>
        <DialogContent
          data-testid="treasury-issue-dialog"
          className="bg-[#0A0906] border border-[#D8B23E]/50 text-[#F5F5F0]"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
              <KeyRound size={18} className="text-[#D8B23E]" />
              Activation Key Dispatched
            </DialogTitle>
            <DialogDescription className="text-[13px] text-[#C9C9C2] font-body pt-2">
              The Vinci Finance Desk email has been sent to <b>{issueRow?.user_email}</b>.
              Below is a preview of the key — full key was never stored plaintext.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="border border-[#D8B23E]/40 bg-black/40 rounded-sm p-4 font-mono text-lg tracking-[0.3em] text-[#D8B23E] text-center">
              {issueRow?.key_preview || "VNC-TSY-••••"}
            </div>
            <div className="text-[11px] text-[#8A8A82] font-body">
              Expires: <span className="text-[#D8B23E]">
                {issueRow?.expires_at ? new Date(issueRow.expires_at).toLocaleString() : "—"}
              </span>
            </div>
            {issueRow?.mail_error && (
              <div className="text-[11px] text-[#E5484D] font-body">
                Mail delivery warning: {issueRow.mail_error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setIssueRow(null)}
                     className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.24em] text-[10px]">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectRow} onOpenChange={(v) => !v && setRejectRow(null)}>
        <DialogContent
          data-testid="treasury-reject-dialog"
          className="bg-[#0A0906] border border-[#E5484D]/50 text-[#F5F5F0]"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
              <XCircle size={18} className="text-[#E5484D]" />
              Reject Treasury Request
            </DialogTitle>
            <DialogDescription className="text-[13px] text-[#C9C9C2] font-body pt-2">
              The partner will be hard-locked until an admin explicitly resets them.
              Reason will be sent to the audit trail.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="treasury-reject-reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason (visible to partner)"
            className="bg-black/40 border-white/20 min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectRow(null)}
                     className="border-white/20 text-[#C9C9C2]">Cancel</Button>
            <Button
              data-testid="treasury-reject-confirm"
              onClick={submitReject}
              disabled={!!busyId}
              className="bg-[#E5484D] text-white font-grotesk uppercase tracking-[0.24em] text-[10px]"
            >
              {busyId ? <Loader2 size={12} className="animate-spin" /> : "Confirm Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TreasuryRow({ row, busy, onIssue, onReject, onReset }) {
  const s = STATUS_STYLE[row.status] || STATUS_STYLE.expired;
  return (
    <motion.div
      data-testid={`treasury-row-${row.id}`}
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className={`border ${s.border} rounded-sm p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-black/20`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded-sm text-[9px] font-grotesk uppercase tracking-[0.22em] border ${s.bg} ${s.fg} ${s.border}`}>
            {s.label}
          </span>
          <span className="font-serif text-lg text-[#F5F5F0]">
            {row.business_name || row.user_email}
          </span>
        </div>
        <div className="mt-1 text-[12px] font-body text-[#8A8A82]">
          {row.user_email}
          {row.requested_at && (
            <span className="ml-2 italic">
              · requested {new Date(row.requested_at).toLocaleDateString()}
            </span>
          )}
        </div>
        {row.status === "key_issued" && row.key_expires_at && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono text-[#D8B23E]">
            <Clock size={11} /> Key expires {new Date(row.key_expires_at).toLocaleString()}
          </div>
        )}
        {row.status === "rejected" && (
          <div className="mt-2 text-[12px] text-[#E5484D] font-body italic">
            Rejected — {row.rejection_reason || "no reason"}
            {row.rejected_by ? ` · by ${row.rejected_by}` : ""}
          </div>
        )}
        {row.status === "activated" && row.activated_at && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono text-[#2FB999]">
            <CheckCircle2 size={11} /> Vault synchronised {new Date(row.activated_at).toLocaleString()}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {(row.status === "requested" || row.status === "expired") && (
          <>
            <Button
              data-testid={`treasury-issue-${row.id}`}
              onClick={onIssue}
              disabled={busy}
              className="bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk uppercase tracking-[0.24em] text-[10px]"
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <><KeyRound size={11} className="mr-1" /> Dispatch Key</>}
            </Button>
            <Button
              data-testid={`treasury-reject-${row.id}`}
              onClick={onReject}
              disabled={busy}
              variant="outline"
              className="border-[#E5484D]/40 text-[#E5484D] hover:bg-[#E5484D]/10 font-grotesk uppercase tracking-[0.24em] text-[10px]"
            >
              <XCircle size={11} className="mr-1" /> Reject
            </Button>
          </>
        )}
        {row.status === "key_issued" && (
          <span className="text-[10px] font-grotesk uppercase tracking-[0.24em] text-[#D8B23E]">
            Awaiting Partner
          </span>
        )}
        {row.status === "rejected" && (
          <Button
            data-testid={`treasury-reset-${row.id}`}
            onClick={onReset}
            disabled={busy}
            variant="outline"
            className="border-white/20 text-[#D8B23E] hover:border-[#D8B23E] font-grotesk uppercase tracking-[0.24em] text-[10px]"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <><RotateCcw size={11} className="mr-1" /> Reset Partner</>}
          </Button>
        )}
      </div>
    </motion.div>
  );
}
